import memberWorkspaceStyles from './member-workspace.styles.css?inline';
import { LitElement, html, nothing, unsafeCSS } from 'lit';
import type { TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { styles as sharedStyles } from '../../../styles/shared-styles';
import { tabbedPageFullscreenStyles } from '../../../styles/tabbed-page-fullscreen.styles';
import { router, resolveRouterPath } from '../../../router';
import {
  getMemberWorkspaceStore,
  MemberWorkspaceStore,
} from './member-workspace-store';
import type { MemberWorkspaceState } from './member-workspace-store';
import { WellKnownIds } from '../../../utility/claim-id';
import { operatingStatusLabel } from '../shared/company-status-display';
import { companiesCrumb } from '../shared/company-trail';
import '../../../components/state-badge/agwa-state-badge';
import '../../../components/agwa-page-header';
import '../../../components/layout/agwa-fullscreen-block';
import {
  listMemberWorkspaceModules,
  type MemberWorkspaceModuleDescriptor,
  type MemberWorkspaceContext,
} from './member-workspace-modules';

import '../../../components/agwa/agwa-action-bar.js';
import '../../../components/agwa/agwa-lock-held-banner.js';
import '../../../components/agwa/agwa-lock-lost-banner.js';
import { RecordEditLock } from '../../../utility/locking/record-edit-lock';
import { COMPANY_LOCK_RESOURCE } from '../shared/company-lock';
import { confirmAction } from '../../../components/dialogs/agwa-confirm-dialog';
import { showErrorToast } from '../../../utility/notifications/easy-toast';
import './member-workspace-overview.js';
import type { MemberWorkspaceOverview } from './member-workspace-overview.js';
import './member-workspace-abilities.js';
import './site-list.js';
import '../../../components/security/domain-module-viewer.js';
import '../../app-settings/user-management/staff-list.js';

// Import domain modules to auto-register workspace tabs
import '../../../domains/index.js';

import '@shoelace-style/shoelace/dist/components/tab-group/tab-group.js';
import '@shoelace-style/shoelace/dist/components/tab/tab.js';
import '@shoelace-style/shoelace/dist/components/tab-panel/tab-panel.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/spinner/spinner.js';
import '@shoelace-style/shoelace/dist/components/tag/tag.js';

import type { LoginDomainScopeGrant } from '../../../api/agwa-api-model';
import { terms } from '../../../language/terms';
import {
  tabGroupScrollElement,
  type PageScrollElementHost,
} from '../../../utility/page-scroll-element';

interface WorkspaceTabDefinition {
  id: string;
  label: string;
  icon?: string;
  disabled?: boolean;
  section?: string;
  content: TemplateResult;
}

type ModuleLoadState = 'idle' | 'loading' | 'loaded' | 'error';

const EMPTY_STATE: MemberWorkspaceState = {
  loading: false,
  error: null,
  group: null,
  imis: null,
  capabilityProfile: null,
  securityError: null,
  inheritedDomains: [],
};

/**
 * Company-level Security and Operation Types tabs are hidden while operation types are
 * site-scoped (architect, 2026-09-05). A flag rather than deletion so the whole surface
 * stays compiled and returns with one edit - the same lever shape D-087 uses for WERS.
 */
const SHOW_COMPANY_LEVEL_SECURITY_TABS = false;

@customElement('member-workspace')
export class MemberWorkspace
  extends LitElement
  implements PageScrollElementHost
{
  static styles = [
    sharedStyles,
    tabbedPageFullscreenStyles,
    unsafeCSS(memberWorkspaceStyles),
  ];

  /**
   * WHERE THIS PAGE SCROLLS (D-131): the tab group's body, not this host. #371 holds the page
   * header, the banners and the tab strip still and scrolls only the active tab's body, so that
   * is the element the shell starts at the top and hands the keyboard to after a navigation.
   */
  get pageScrollElement(): HTMLElement | null {
    return tabGroupScrollElement(this);
  }

  @property({ type: String }) groupId = '';
  @property({ type: String }) tab = 'overview';

  @state() private storeState: MemberWorkspaceState = { ...EMPTY_STATE };
  @state() private activeTabState = 'overview';

  private store: MemberWorkspaceStore | null = null;
  private storeListener: (() => void) | null = null;
  private moduleLoadState = new Map<string, ModuleLoadState>();
  /**
   * Each module's `alsoShowWhen` answer for the company on screen, by module id. Empty until the
   * answers land, so such a tab appears once its read has said yes rather than flickering in.
   */
  @state() private extraGates = new Map<string, boolean>();
  /**
   * The company the answers above belong to; null until the first answers land. Until it names
   * the company on screen the answers are not used, and the tab the address asked for is held
   * rather than replaced (ensureActiveTab), because a tab only this gate opens is not in the
   * strip yet: without the hold, reloading or deep-linking onto such a tab landed on Overview.
   */
  @state() private extraGatesSettledFor: string | null = null;
  /** The company and the sites' domain list the answers above were asked for. */
  private extraGatesAskedFor: { groupId: string; inherited: string[] } | null =
    null;

  connectedCallback() {
    super.connectedCallback();
    this.attachStore();
    this.syncActiveTabState();
    void this.showActiveTab();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.detachStore();
  }

  // ─── The company edit lock (#398 C11, @LOCK-033 to 038) ────────────────────────────────────────
  //
  // A company's details edit and its Block / Unblock are made under the company's edit lock, so two
  // staff cannot save over each other; the server refuses a company save from a session that does
  // not hold it (D-115). The page takes it for an Edit and releases it on Save or Cancel. A Block
  // takes nothing here: the server takes the lock for the change itself and releases it after
  // (#406, @LOCK-038, @LOCK-070). The page watches the lock so another person's edit is named here
  // and the actions that would collide with it are withdrawn while it lasts (@LOCK-033).

  /**
   * The company's lock (RecordEditLock): another person's lock names them and withdraws Edit and
   * Block (@LOCK-033); the lock this tab held being lost closes an open edit, since it can no longer
   * be saved (@LOCK-037); another person's lock ending re-reads the company, so the values they
   * saved appear here without a reload (@LOCK-034).
   */
  private readonly companyLock = new RecordEditLock(
    this,
    COMPANY_LOCK_RESOURCE,
    {
      onLost: () => {
        if (this.detailsEditState.editing) {
          this.overviewPanel()?.cancelEdit();
        }
      },
      onOtherReleased: () => void this.store?.reload(),
    }
  );

  /**
   * Watches the company's lock. The AGWA system tenant is never watched: its page offers no Edit or
   * Block, and the server refuses its lock.
   */
  private watchCompanyLock() {
    this.companyLock.watch(
      this.groupId.toLowerCase() === WellKnownIds.agwaGroupId
        ? ''
        : this.groupId
    );
  }

  /**
   * Reads the company again and waits for the overview to carry it, so an edit (once its lock is
   * held) or a block (before its confirmation) starts from what is saved now. Without it, a page
   * that missed another person's release (its live stream down, or a click before the re-read
   * landed) would write its older copy over their save, the overwrite the lock exists to prevent
   * (#398 C11 review 1, F1), or offer Block for a company somebody has just blocked.
   */
  private async rereadCompany(): Promise<void> {
    await this.store?.reload();
    await this.updateComplete;
    await this.overviewPanel()?.updateComplete;
  }

  updated(changedProps: Map<string, unknown>) {
    if (changedProps.has('groupId')) {
      this.attachStore();
    }
    if (changedProps.has('tab')) {
      this.syncActiveTabState();
      void this.showActiveTab();
    }
    // When store state changes (e.g., after loading completes), try to show the active tab
    // This ensures module tabs are shown correctly after page refresh
    if (changedProps.has('storeState')) {
      void this.showActiveTab();
    }
    if (changedProps.has('groupId') || changedProps.has('storeState')) {
      void this.evaluateExtraGates();
    }
    // A tab only a module's alsoShowWhen opens joins the strip when its answer lands, after the
    // store has settled; the address's tab is shown then, or a reload on it would keep the URL
    // but show Overview.
    if (changedProps.has('extraGatesSettledFor')) {
      void this.showActiveTab();
    }
  }

  /**
   * Ask every module with an `alsoShowWhen` about this company, when the company changes or its
   * sites' domains are re-read (a registration or a site going inactive changes both answers).
   * Any other store change keeps the answers it has. A late answer for a company no longer on
   * screen is dropped rather than applied to the next one.
   */
  private async evaluateExtraGates() {
    const groupId = this.groupId;
    const inherited = this.storeState.inheritedDomains;
    const asked = this.extraGatesAskedFor;
    if (asked && asked.groupId === groupId && asked.inherited === inherited) {
      return;
    }
    this.extraGatesAskedFor = { groupId, inherited };
    if (!groupId) {
      this.extraGates = new Map();
      return;
    }
    const gated = listMemberWorkspaceModules().filter(
      (descriptor) => descriptor.alsoShowWhen
    );
    const answers = await Promise.all(
      gated.map(async (descriptor) => {
        try {
          return [
            descriptor.id,
            await descriptor.alsoShowWhen!(groupId),
          ] as const;
        } catch {
          return [descriptor.id, false] as const;
        }
      })
    );
    if (this.extraGatesAskedFor?.groupId !== groupId) return;
    this.extraGates = new Map(answers);
    this.extraGatesSettledFor = groupId;
  }

  private attachStore() {
    this.detachStore();
    if (!this.groupId) {
      this.storeState = { ...EMPTY_STATE };
      return;
    }
    this.watchCompanyLock();
    const store = getMemberWorkspaceStore(this.groupId);
    this.store = store;
    this.storeState = { ...store.snapshot };
    this.storeListener = () => {
      this.storeState = { ...store.snapshot };
      this.requestUpdate();
    };
    store.addEventListener('change', this.storeListener);
    void store.ensureHydrated();
  }

  private detachStore() {
    // The lock is the company's, so it goes with the company: a page moving to another company
    // watches that one instead (watchCompanyLock), and leaving the page lets go of it
    // (RecordEditLock.hostDisconnected).
    if (this.store && this.storeListener) {
      this.store.removeEventListener('change', this.storeListener);
    }
    this.storeListener = null;
    this.store = null;
  }

  private syncActiveTabState() {
    const routeTab = this.tab || 'overview';
    // Map old 'features' route to 'security' tab
    const normalized = routeTab === 'features' ? 'security' : routeTab;
    if (this.activeTabState !== normalized) {
      this.activeTabState = normalized;
    }
  }

  private async showActiveTab() {
    await this.updateComplete;
    const tabGroup = this.renderRoot.querySelector(
      'sl-tab-group'
    ) as HTMLElement & { show?: (name: string) => void };
    if (tabGroup?.show && this.activeTabState) {
      tabGroup.show(this.activeTabState);
    }
  }

  private handleTabShow(event: CustomEvent<{ name: string }>) {
    const targetTab = event.detail?.name;
    if (!targetTab || targetTab === this.activeTabState) {
      return;
    }
    this.activeTabState = targetTab;
    this.navigateToTab(targetTab);
  }

  private navigateToTab(tabId: string) {
    if (!this.groupId) {
      return;
    }
    const base = `maintenance/companies/${this.groupId}`;
    const suffix = tabId && tabId !== 'overview' ? `/${tabId}` : '';
    void router?.navigate(resolveRouterPath(`${base}${suffix}`));
  }

  private async handleRefreshSecurity() {
    if (!this.store) {
      return;
    }
    await this.store.refreshSecurity();
  }

  private get domains(): LoginDomainScopeGrant[] {
    return this.storeState.capabilityProfile?.securityContext?.domains ?? [];
  }

  private get isSystemTenantGroup() {
    return (
      this.storeState.group?.id?.toLowerCase() === WellKnownIds.agwaGroupId
    );
  }

  private buildContext(): MemberWorkspaceContext {
    return {
      group: this.storeState.group,
      imis: this.storeState.imis,
      capabilityProfile: this.storeState.capabilityProfile,
      securityError: this.storeState.securityError,
      loading: this.storeState.loading,
      domains: this.domains,
      inheritedDomains: this.storeState.inheritedDomains,
      refreshSecurity: () => this.handleRefreshSecurity(),
    };
  }

  private baseTabs(): WorkspaceTabDefinition[] {
    const context = this.buildContext();
    return [
      {
        id: 'overview',
        label: 'Overview',
        icon: 'house',
        content: html`<member-workspace-overview
          .loading=${this.storeState.loading}
          .group=${context.group}
          .imis=${context.imis}
          .domains=${context.domains}
          .store=${this.store}
          @editing-changed=${this.handleEditingChanged}
        ></member-workspace-overview>`,
      },
      {
        id: 'staff',
        label: terms.staffUser.plural,
        icon: 'people',
        disabled: this.isSystemTenantGroup,
        content: html`<div class="tab-panel-body">
          ${
            this.isSystemTenantGroup
              ? html`<sl-alert variant="neutral" open>
                  The ${terms.agwa.value} system tenant manages its
                  ${terms.staffUser.lowerPlural} through internal tooling.
                </sl-alert>`
              : // `rail-scope-tab` gives this instance its own memory of whether the filter
                // rail is folded, so folding it on this tab does not fold it on the AGWA
                // Staff page - two working surfaces with different room beside the table
                // (D-102).
                // `embedded` because the panel around it already carries the gutter: without
                // it the list draws a second one, which is white space under the tab strip and
                // height taken off the table.
                html`<staff-list
                  .groupId=${this.groupId}
                  .returnUrl=${`/maintenance/companies/${this.groupId}/staff`}
                  rail-scope-tab
                  embedded
                ></staff-list>`
          }
        </div>`,
      },
      {
        id: 'sites',
        label: terms.loginSite.plural,
        icon: 'building',
        disabled: this.isSystemTenantGroup,
        content: html`<div class="tab-panel-body">
          ${
            this.isSystemTenantGroup
              ? html`<sl-alert variant="neutral" open>
                  The ${terms.agwa.value} system tenant does not manage
                  ${terms.loginSite.lowerPlural}.
                </sl-alert>`
              : html`<site-list .groupId=${this.groupId}></site-list>`
          }
        </div>`,
      },
      // HIDDEN, NOT DELETED (architect, 2026-09-05, the same lever shape as D-087's WERS
      // hide): operation types are site-scoped now, so neither Security nor Operation Types
      // has anything to manage at the company level - the overview shows a readonly
      // reflection of the sites instead (company-operation-types-section), and roles follow
      // site registrations. Everything stays compiled behind this flag; flip it to true to
      // reintroduce both tabs whole.
      ...(SHOW_COMPANY_LEVEL_SECURITY_TABS
        ? [
            {
              id: 'security',
              label: 'Security',
              icon: 'shield-check',
              content: this.renderSecurityTab(context),
            },
            {
              id: 'abilities',
              label: terms.userGroupDomain.plural,
              icon: 'diagram-3',
              content: html`<member-workspace-abilities
                .groupId=${this.groupId}
                @abilities-updated=${this.handleRefreshSecurity}
              ></member-workspace-abilities>`,
            },
          ]
        : []),
    ];
  }

  private renderSecurityTab(context: MemberWorkspaceContext) {
    // Build domain assignments from the context
    const domainAssignments = context.domains.map((d) => ({
      domainCode: d.domainCode || '',
      domainDisplayName: d.domainDisplayName || d.domainCode || '',
    }));

    // Build module access from role assignments
    const roleAssignments =
      context.capabilityProfile?.access?.getRoleAssignments() ?? [];
    const moduleMap = new Map<
      string,
      {
        moduleCode: string;
        moduleDisplayName: string;
        accessType: 'owner' | 'contributor' | 'viewer' | 'consumer';
        roles: string[];
      }
    >();

    for (const ra of roleAssignments) {
      const moduleCode = ra.moduleCode || ra.moduleId || '';
      if (!moduleCode) continue;

      const existing = moduleMap.get(moduleCode);
      const roleCode = ra.code || ra.displayName || '';
      if (existing) {
        if (roleCode && !existing.roles.includes(roleCode)) {
          existing.roles.push(roleCode);
        }
      } else {
        moduleMap.set(moduleCode, {
          moduleCode,
          moduleDisplayName: ra.moduleDisplayName || moduleCode,
          accessType: this.inferAccessType(roleCode),
          roles: roleCode ? [roleCode] : [],
        });
      }
    }

    const moduleAccess = Array.from(moduleMap.values());

    return html`<div class="tab-panel-body security-panel">
      <div class="security-header">
        <div>
          <h3>Security Overview</h3>
          <p class="muted">
            ${terms.userGroupDomain.plural}, modules, and roles assigned to this
            ${this.isSystemTenantGroup ? 'tenant' : terms.loginGroup.lower}.
          </p>
        </div>
        <sl-button size="small" @click=${this.handleRefreshSecurity}>
          <sl-icon slot="prefix" name="arrow-clockwise"></sl-icon>
          Refresh
        </sl-button>
      </div>
      ${
        context.securityError
          ? html`<sl-alert variant="warning" open
              >${context.securityError}</sl-alert
            >`
          : nothing
      }
      <domain-module-viewer
        .domains=${domainAssignments}
        .modules=${moduleAccess}
        .loading=${context.loading}
        .entityType=${'group' as const}
      ></domain-module-viewer>
    </div>`;
  }

  private inferAccessType(
    roleCode: string
  ): 'owner' | 'contributor' | 'viewer' | 'consumer' {
    const lower = roleCode.toLowerCase();
    if (lower.includes('owner') || lower.includes('creator')) return 'owner';
    if (lower.includes('entry') || lower.includes('contributor'))
      return 'contributor';
    if (lower.includes('auditor') || lower.includes('viewer')) return 'viewer';
    return 'consumer';
  }

  private buildModuleTabs(context: MemberWorkspaceContext) {
    const domains = new Set(
      context.domains
        .filter((domain) => domain.active !== false) // Only include active domains
        .map((domain) => domain.domainCode?.toLowerCase())
        .filter((code): code is string => !!code)
    );

    const inherited = new Set(
      context.inheritedDomains.map((code) => code.toLowerCase())
    );

    const descriptors = listMemberWorkspaceModules().filter((descriptor) => {
      if (descriptor.systemOnly && !this.isSystemTenantGroup) {
        return false;
      }
      const wantsGroup = (descriptor.domains?.length ?? 0) > 0;
      const wantsInherited = (descriptor.inheritedDomains?.length ?? 0) > 0;
      if (!wantsGroup && !wantsInherited && !descriptor.alsoShowWhen) {
        return true;
      }
      // Any gate opens the tab: what the company holds itself, what its sites hold, or the
      // module's own further reason for this company.
      return (
        (this.extraGatesSettledFor === this.groupId &&
          this.extraGates.get(descriptor.id) === true) ||
        (descriptor.domains ?? []).some((code) =>
          domains.has(code.toLowerCase())
        ) ||
        (descriptor.inheritedDomains ?? []).some((code) =>
          inherited.has(code.toLowerCase())
        )
      );
    });

    return descriptors.map((descriptor) => {
      this.ensureModuleLoaded(descriptor);
      const state = this.moduleLoadState.get(descriptor.id) ?? 'idle';
      let content: TemplateResult;
      if (state === 'error') {
        content = html`<sl-alert variant="danger" open>
          Unable to load ${descriptor.label}. Check the console for details.
        </sl-alert>`;
      } else if (
        state === 'loading' ||
        (descriptor.load && state !== 'loaded')
      ) {
        content = html`<div class="module-loading">
          <sl-spinner></sl-spinner>
          <span>Loading ${descriptor.label}…</span>
        </div>`;
      } else {
        content = descriptor.render(context);
      }
      return {
        id: descriptor.id,
        label: descriptor.label,
        icon: descriptor.icon ?? 'puzzle',
        section: descriptor.section,
        content,
      } satisfies WorkspaceTabDefinition;
    });
  }

  private ensureModuleLoaded(descriptor: MemberWorkspaceModuleDescriptor) {
    if (!descriptor.load) {
      this.moduleLoadState.set(descriptor.id, 'loaded');
      return;
    }
    const current = this.moduleLoadState.get(descriptor.id);
    if (current === 'loading' || current === 'loaded') {
      return;
    }
    this.moduleLoadState.set(descriptor.id, 'loading');
    descriptor
      .load()
      .then(() => {
        this.moduleLoadState.set(descriptor.id, 'loaded');
        this.requestUpdate();
      })
      .catch((error) => {
        console.error(
          `Failed to load workspace module ${descriptor.id}`,
          error
        );
        this.moduleLoadState.set(descriptor.id, 'error');
        this.requestUpdate();
      });
  }

  private computeTabs() {
    const context = this.buildContext();
    return [...this.baseTabs(), ...this.buildModuleTabs(context)];
  }

  private ensureActiveTab(tabs: WorkspaceTabDefinition[]) {
    if (tabs.length === 0) {
      return 'overview';
    }
    const desired = this.activeTabState || 'overview';
    const hasMatch = tabs.some((tab) => tab.id === desired && !tab.disabled);
    if (hasMatch) {
      return desired;
    }
    // Don't fallback-navigate while still loading - module tabs may not be available yet
    // This prevents losing the URL on page refresh before domains are loaded. The modules'
    // own alsoShowWhen answers count as loading until they land for this company.
    if (
      this.storeState.loading ||
      !this.storeState.group ||
      this.extraGatesSettledFor !== this.groupId
    ) {
      return desired;
    }
    const fallback = tabs.find((tab) => !tab.disabled)?.id ?? tabs[0].id;
    if (fallback) {
      this.activeTabState = fallback;
      if (this.tab !== fallback) {
        this.navigateToTab(fallback);
      }
      return fallback;
    }
    return desired;
  }

  private renderTabNav(tabs: WorkspaceTabDefinition[]) {
    const result: TemplateResult[] = [];
    const seenSections = new Set<string>();

    for (const tab of tabs) {
      // Section headers were rows in the old vertical rail; the horizontal bar (the product
      // page's idiom, architect 2026-09-05) has no place for them, so grouping is carried by
      // tab order alone. seenSections stays so the sections return with a vertical layout.
      if (tab.section && !seenSections.has(tab.section)) {
        seenSections.add(tab.section);
      }
      result.push(html`
        <sl-tab slot="nav" panel=${tab.id} ?disabled=${tab.disabled}>
          ${
            tab.icon
              ? html`<sl-icon slot="prefix" name=${tab.icon}></sl-icon>`
              : nothing
          }
          ${tab.label}
        </sl-tab>
      `);
    }
    return result;
  }

  /**
   * `Companies > {company}`, the company as the leaf with its username prefix and its
   * operating status beside it (the trail standard, architect 2026-09-06). The list link is
   * a real anchor in the shared header, which is what retired this page's own arrow button.
   */
  private renderMemberBanner() {
    const group = this.storeState.group;
    if (!group) {
      return nothing;
    }
    return html`
      <agwa-page-header class="member-banner" .crumbs=${[companiesCrumb()]}>
        <span class="member-banner-name">${group.name}</span>
        <span slot="badges" class="member-banner-badges">
          ${
            group.userNamePrefix
              ? html`<span class="member-banner-prefix"
                  >(${group.userNamePrefix})</span
                >`
              : nothing
          }
          <!--
            Read from the same place as the chip in the details card below it. They disagreed for
            one commit - the banner said "Inactive" while the card two inches under it said
            "Blocked", for the same company and the same flag - which is exactly the drift the
            shared module exists to make impossible, and it was a journey that noticed rather than
            a person.
          -->
          <agwa-state-badge
            domain="companyOperating"
            state=${operatingStatusLabel(group.active)}
          ></agwa-state-badge>
        </span>
        ${this.renderActionBar()}
      </agwa-page-header>
    `;
  }

  /**
   * The company lock's two banners, in the wording every lockable page uses: the lock this tab held
   * is gone (@LOCK-037), or somebody else is editing this company (@LOCK-033). About the company, not
   * a tab, so they sit under the header with it.
   */
  private renderCompanyLockBanners() {
    return html`
      ${
        this.companyLock.loss
          ? html`<agwa-lock-lost-banner
              noun=${terms.loginGroup.lower}
              taken-over-by-user-id=${this.companyLock.loss.takenOverByUserId ?? ''}
              @dismiss=${() => this.companyLock.dismissLoss()}
            ></agwa-lock-lost-banner>`
          : nothing
      }
      ${
        this.companyLock.lockedByOther
          ? html`<agwa-lock-held-banner
              noun=${terms.loginGroup.lower}
              holder-user-id=${this.companyLock.holderUserId ?? ''}
              .leaseExpiresAt=${this.companyLock.expiresAt}
            ></agwa-lock-held-banner>`
          : nothing
      }
    `;
  }

  private renderTabs(tabs: WorkspaceTabDefinition[], activeTab: string) {
    if (tabs.length === 0) {
      return html`<div class="module-placeholder">
        No modules are available for this ${terms.loginGroup.lower} yet.
      </div>`;
    }
    return html`<sl-tab-group
      class="tab-shell"
      .activeTab=${activeTab}
      @sl-tab-show=${this.handleTabShow}
    >
      ${this.renderTabNav(tabs)}
      ${
        this.isSystemTenantGroup
          ? html`<div slot="nav" class="system-tenant-tag">
              <sl-tag pill variant="neutral" size="small">System Tenant</sl-tag>
            </div>`
          : nothing
      }
      ${tabs.map(
        (tab) =>
          html`<sl-tab-panel name=${tab.id}>${tab.content}</sl-tab-panel>`
      )}
    </sl-tab-group>`;
  }

  render() {
    const tabs = this.computeTabs();
    const activeTab = this.ensureActiveTab(tabs);
    // The banner and the error above it are about the COMPANY rather than about a tab, so they
    // ride in the wrapper's header slot and stay on screen inside the takeover; a workspace with
    // no modules has no tab group for the wrapper to hold, so it keeps the plain shape (#373).
    const chrome = html`
      ${
        this.storeState.error
          ? html`<sl-alert variant="danger" open
              >${this.storeState.error}</sl-alert
            >`
          : nothing
      }
      ${this.renderMemberBanner()} ${this.renderCompanyLockBanners()}
    `;
    if (tabs.length === 0) {
      return html`<div class="workspace-root">
        ${chrome} ${this.renderTabs(tabs, activeTab)}
      </div>`;
    }
    return html`<div class="workspace-root">
      <!--
        THE TAB GROUP IS ONE BLOCK THAT CAN TAKE THE WHOLE VIEWPORT IN PLACE (#373, D-106).
      -->
      <agwa-fullscreen-block
        class="page-takeover"
        .title=${terms.loginGroup.value}
      >
        <div class="page-chrome" slot="header">${chrome}</div>
        ${this.renderTabs(tabs, activeTab)}
      </agwa-fullscreen-block>
    </div>`;
  }

  @state() private togglingActive = false;

  /** Mirrors the overview's edit state so the bar can swap its actions. */
  @state() private detailsEditState = { editing: false, saving: false };

  private handleEditingChanged(
    e: CustomEvent<{ editing: boolean; saving: boolean }>
  ) {
    this.detailsEditState = e.detail;
  }

  private overviewPanel(): MemberWorkspaceOverview | null {
    return this.renderRoot.querySelector('member-workspace-overview');
  }

  /**
   * The workspace's actions, the product detail page's idiom (architect, 2026-09-05:
   * "the tabbing and label value layout and menu bar feels like a cleaner design"). They
   * ride the header's actions slot, right-aligned on the breadcrumb line, rather than
   * owning a framed row of their own (architect, 2026-09-06: "to make room the action bar
   * should be right aligned on the same line as breadcrumb"). Block / Unblock moves up here
   * from the details card - it acts on the whole company, not on the fields the card
   * edits. The bar's own confirm machinery is deliberately NOT used: the
   * D-036 stage-2 confirmation copy ("Yes, Block", who it reaches) is a ruled contract that
   * @CO-MEMBER-004 clicks by name, so the existing confirmAction flow runs unchanged.
   */
  private renderActionBar() {
    const group = this.storeState.group;
    if (!group || this.isSystemTenantGroup) return nothing;
    // While another person edits this company its Edit and Block are withdrawn (@LOCK-033): the
    // banner says who, and both would be refused by the lock anyway. As the report pages do.
    if (this.companyLock.lockedByOther && !this.detailsEditState.editing) {
      return nothing;
    }
    const blocked = !group.active;
    // Edit belongs to the Overview tab's details card, so it only offers itself there;
    // Block acts on the whole company and stays on every tab. While editing, the bar IS
    // the edit's controls - Save/Cancel replace everything, so a half-finished edit
    // cannot be blocked out from under itself (architect, 2026-09-05: every action on
    // the bar, consistent across product, company and site).
    const onOverview = (this.activeTabState || 'overview') === 'overview';
    const actions = this.detailsEditState.editing
      ? [
          {
            id: 'save-details',
            label: 'Save Changes',
            icon: 'check2',
            variant: 'primary' as const,
          },
          { id: 'cancel-edit', label: 'Cancel' },
        ]
      : [
          {
            id: 'toggle-active',
            label: blocked ? 'Unblock' : 'Block',
            icon: blocked ? 'unlock' : 'slash-circle',
            variant: blocked ? ('success' as const) : ('danger' as const),
          },
          ...(onOverview
            ? [{ id: 'edit-details', label: 'Edit', icon: 'pencil' }]
            : []),
        ];
    return html`<agwa-action-bar
      inline
      slot="actions"
      .loading=${this.togglingActive || this.detailsEditState.saving}
      .actions=${actions}
      @action=${this.handleActionBarAction}
    ></agwa-action-bar>`;
  }

  private async handleActionBarAction(e: CustomEvent<{ id: string }>) {
    switch (e.detail.id) {
      case 'toggle-active':
        await this.handleToggleActive();
        break;
      // Edit takes the company's lock first and opens the form only once it is held, on the company as
      // read after taking it; Save releases it only after the save landed; Cancel writes nothing and
      // releases it (@LOCK-033 to 035).
      // A re-read that fails leaves no company to edit, so the lock is let go rather than held with
      // no form open, keeping everyone else out (#398 C11 review 2, R1).
      case 'edit-details':
        if (await this.companyLock.take()) {
          let opened = false;
          try {
            await this.rereadCompany();
            if (this.storeState.group) {
              this.overviewPanel()?.startEdit();
              opened = this.detailsEditState.editing;
            }
          } finally {
            if (!opened) await this.companyLock.release();
          }
        }
        break;
      case 'save-details':
        if (await this.overviewPanel()?.saveEdit()) {
          await this.companyLock.release();
        }
        break;
      case 'cancel-edit':
        this.overviewPanel()?.cancelEdit();
        await this.companyLock.release();
        break;
    }
  }

  /**
   * Moved whole from member-workspace-overview when the button moved to the action bar
   * (2026-09-05); the words and the warning copy are D-036 stage 2's and unchanged.
   */
  private async handleToggleActive() {
    if (!this.storeState.group || !this.store || this.togglingActive) {
      return;
    }
    // No lock is taken here: the server takes the company's lock for the block itself, and refuses
    // it, naming the holder, while somebody else's edit is open (#406, @LOCK-038, @LOCK-070). The
    // write carries only the company's id and whether it is blocked (@LOCK-066). The company is read
    // again first so the toggle goes the way it now stands and the confirmation names it as now
    // saved, never as this page loaded it before somebody's save.
    await this.rereadCompany();
    const group = this.storeState.group;
    if (!group) return;
    await this.confirmAndSetActive(group.name, !group.active);
  }

  /** The block / unblock confirmation and the write; the server takes the company's lock for it. */
  private async confirmAndSetActive(groupName: string, newActive: boolean) {
    if (!this.store) return;
    const confirmed = await confirmAction({
      title: newActive
        ? `Unblock ${terms.loginGroup.value}`
        : `Block ${terms.loginGroup.value}`,
      message: newActive
        ? `Unblock "${groupName}"? Everyone there will be able to sign in again, with exactly what they could do before - nothing has to be set up again.`
        : `Block "${groupName}"? Nobody there will be able to sign in, whatever their own account state, and anyone already signed in will stop being able to work.`,
      confirmText: newActive ? 'Yes, Unblock' : 'Yes, Block',
      cancelText: 'Cancel',
      variant: newActive ? 'success' : 'danger',
    });
    if (!confirmed) {
      return;
    }
    this.togglingActive = true;
    try {
      const answer = await this.store.setActive(newActive);
      // The server's reason, so the person knows why it was not done (#406, @LOCK-065), with
      // another person's open edit named as that person: the reason goes to the toast as the
      // server sent it, which shows a person tag as their current name (@LOCK-068,
      // @VOC-SERVER-002). Nothing for a request that got no answer: the connection monitor tells
      // the person (D-144).
      if (!answer.ok && !answer.unanswered) {
        showErrorToast('Update Failed', answer.errorKept ?? answer.error);
      }
    } finally {
      this.togglingActive = false;
    }
  }
}
